import { set } from 'lodash'
import {
  addOrUpdateRouterWithPattern,
  isClassicEditor,
} from '@wix/wix-data-client-common'
import { ensurePageIsLegacyIndexable } from '../../ensurePageIsLegacyIndexable'
import { makeItemPageNonIndexable } from '../../makeItemPageNonIndexable'

import type { HttpClient } from '@wix/http-client'
import type { EditorSDK, Logger } from '@wix/wix-data-client-common-standalone'
import type {
  AppApi,
  EDITOR_TYPE,
  PageType,
  PatternDefinition,
} from '@wix/wix-data-client-common'
import type { PageRef } from '@wix/editor-platform-sdk-types'
import type { SerializedPageStructure } from '@wix/document-services-types'
import { addRouterDataset } from './addRouterDataset'
import { fetchRouterDatasetFromPage } from '../../fetchRouterDatasetFromPage'

export type CreatePreset = (params: {
  collectionName: string
  routerId: string
}) => SerializedPageStructure

interface CreateOrConnectDynamicPageParams {
  managingAppDefId: string
  editorSdkProxy: EditorSDK
  appApi: AppApi
  prefix: string
  pattern: string
  pageType: PageType
  pageRef: PageRef | undefined
  createPreset: CreatePreset
  routeDefinition: PatternDefinition
  pageTitle: string
  isEditPage: boolean
  collectionName: string
  editorType: EDITOR_TYPE
  httpClient: HttpClient
  logger: Logger
  presetId: string
}

export const createOrConnectDynamicPage = async ({
  managingAppDefId,
  editorSdkProxy,
  appApi,
  prefix,
  pattern,
  pageType,
  pageRef,
  createPreset,
  routeDefinition,
  pageTitle,
  isEditPage,
  collectionName,
  editorType,
  httpClient,
  logger,
  presetId,
}: CreateOrConnectDynamicPageParams) => {
  const routerRefBeforeAddOrUpdate = await editorSdkProxy.routers.getByPrefix({
    prefix,
  })

  const routerRef = await addOrUpdateRouterWithPattern(
    editorSdkProxy,
    prefix,
    pattern,
    routeDefinition,
  )
  const router = await editorSdkProxy.routers.get({ routerRef })

  const allRouters = await editorSdkProxy.routers.getAll()

  logger.breadcrumb({
    category: 'createOrConnectDynamicPage',
    message: 'After adding or updating router',
    data: {
      routerRef,
      routerRefBeforeAddOrUpdate,
      routeDefinition,
      prefix,
      pattern,
      pageType,
      pageRef,
      pageTitle,
      presetId,
      editorType,
      allRouters,
    },
  })

  if (pageRef) {
    await editorSdkProxy.document.pages.data.update({
      pageRef,
      data: { parentPageId: undefined },
    })
  } else {
    const definition = set(
      createPreset({ collectionName, routerId: router.id }),
      'data.pageUriSEO',
      '',
    )

    pageRef = await editorSdkProxy.document.pages.add({
      title: pageTitle,
      definition,
      shouldAddMenuItem: true,
      shouldNavigateToPage: false,
    })

    if (isClassicEditor(editorType)) {
      const routerDataset = await fetchRouterDatasetFromPage(
        editorSdkProxy,
        pageRef,
      )
      if (!routerDataset) {
        throw new Error(
          'Router dataset not found on page created from createPreset',
        )
      }
      await editorSdkProxy.document.controllers.moveAppControllerToPage({
        controllerRef: routerDataset.ref,
        pageRef,
      })
    }
  }

  await editorSdkProxy.routers.pages.connect({
    routerRef,
    pageRef,
    pageRoles: [routeDefinition.pageRole],
  })

  await editorSdkProxy.pages.rename({ pageRef, title: pageTitle })

  if (isEditPage) {
    await makeItemPageNonIndexable({ httpClient, pageId: pageRef.id })
  } else {
    await ensurePageIsLegacyIndexable({
      editorSdkProxy,
      pageRef,
    })
  }

  if (!createPreset) {
    await addRouterDataset({
      editorSdkProxy,
      appApi,
      editorType,
      pageRef,
      collectionName,
      pageType,
    })
  }

  await editorSdkProxy.document.pages.data.update({
    pageRef,
    data: { managingAppDefId },
  })

  return { pageRef }
}
